import { useRef, useState } from 'react';
import { useLingui } from '@lingui/react';
import { BellRing, MoreHorizontal, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getActionLabel } from '@/types/workflow';

interface WorkflowRowActionsMenuProps {
  rowId: string;
  /** The item's available_actions. */
  actions: string[];
  canHandOver: boolean;
  canRemind: boolean;
  onHandOver: () => void;
  onRemind: () => void;
  onAction: (action: string) => void;
}

/**
 * The "..." menu of an inbox row. Every choice opens a dialog, so the menu is
 * controlled: a choice keeps Radix from closing the menu itself
 * (`preventDefault`), closes it through state, and runs the choice only once
 * the menu has closed and handed focus back to its trigger. A dialog that
 * mounted in the same tick as the closing menu could leave `pointer-events:
 * none` on the body or the focus stuck after the dialog closes.
 */
export function WorkflowRowActionsMenu({
  rowId,
  actions,
  canHandOver,
  canRemind,
  onHandOver,
  onRemind,
  onAction,
}: WorkflowRowActionsMenuProps) {
  const { i18n } = useLingui();
  const [open, setOpen] = useState(false);
  const pending = useRef<(() => void) | null>(null);

  if (actions.length === 0 && !canHandOver) return null;

  const choose = (run: () => void) => (event: Event) => {
    event.preventDefault();
    pending.current = run;
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={i18n._({ id: 'workflow.column.actions.menu', message: 'Open actions menu' })}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        onClick={(e) => e.stopPropagation()}
        onCloseAutoFocus={() => {
          // The menu is gone and focus goes back to the trigger; the dialog
          // opens after that, so it returns focus there when it closes.
          const run = pending.current;
          pending.current = null;
          if (run) window.setTimeout(run, 0);
        }}
      >
        {canHandOver && (
          <DropdownMenuItem onSelect={choose(onHandOver)} data-testid={`hand-over-row-${rowId}`}>
            <UserPlus className="mr-2 h-4 w-4" aria-hidden="true" />
            {i18n._({ id: 'workflow.action.handOverMenu', message: 'Hand over...' })}
          </DropdownMenuItem>
        )}
        {canRemind && (
          <DropdownMenuItem onSelect={choose(onRemind)} data-testid={`remind-row-${rowId}`}>
            <BellRing className="mr-2 h-4 w-4" aria-hidden="true" />
            {i18n._({ id: 'workflow.action.remindMenu', message: 'Remind...' })}
          </DropdownMenuItem>
        )}
        {canHandOver && actions.length > 0 && <DropdownMenuSeparator />}
        {actions.map((action) => (
          <DropdownMenuItem
            key={action}
            className={action === 'escalate' ? 'text-destructive focus:text-destructive' : undefined}
            onSelect={choose(() => onAction(action))}
          >
            {getActionLabel(i18n, action)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
